import { useEffect, useRef } from "react";

import { Button } from "@/components/ui/button";

interface DownloadAppModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function DownloadAppModal({ open, onOpenChange }: DownloadAppModalProps) {
	const modalRef = useRef<HTMLDivElement>(null);

	const onClose = () => onOpenChange(false);

	// Lock scroll and set aria/inert on background when modal is open
	useEffect(() => {
		if (open) {
			const originalOverflow = document.body.style.overflow;
			document.body.style.overflow = "hidden";
			const appContent = document.getElementById("app-content");
			if (appContent) {
				appContent.setAttribute("aria-hidden", "true");
			}
			if (modalRef.current) {
				modalRef.current.focus();
			}
			const handleKeyDown = (e: KeyboardEvent) => {
				if (e.key === "Tab" && modalRef.current) {
					const focusable = modalRef.current.querySelectorAll<HTMLElement>(
						"button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])"
					);
					const first = focusable[0];
					const last = focusable[focusable.length - 1];
					if (!focusable.length)
						return;
					if (e.shiftKey) {
						if (document.activeElement === first) {
							e.preventDefault();
							last.focus();
						}
					}
					else {
						if (document.activeElement === last) {
							e.preventDefault();
							first.focus();
						}
					}
				}
			};
			document.addEventListener("keydown", handleKeyDown);
			return () => {
				document.body.style.overflow = originalOverflow;
				if (appContent) {
					appContent.removeAttribute("aria-hidden");
				}
				document.removeEventListener("keydown", handleKeyDown);
			};
		}
	}, [open]);

	if (!open)
		return null;

	return (
		<div
			ref={modalRef}
			className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn"
			onClick={onClose}
			tabIndex={-1}
			onKeyDown={(e) => {
				if (e.key === "Escape") {
					onClose();
				}
			}}
		>
			<div
				className="relative bg-white shadow-2xl border border-[#ececff] animate-scaleIn text-center p-4 rounded-2xl w-full max-w-xs sm:p-6 sm:rounded-3xl sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl"
				onClick={e => e.stopPropagation()}
				style={{ boxShadow: "0 8px 32px rgba(63,46,200,0.12)", margin: "12vh 0", minHeight: "auto", maxHeight: "76vh", overflowY: "auto" }}
			>
				{/* Close button */}
				<button
					className="rounded-full p-1 transition-colors duration-150 hover:bg-gray-200"
					style={{ position: "absolute", top: 12, right: 16, lineHeight: 1, border: "none", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}
					onClick={onClose}
					aria-label="Close"
				>
					<img
						src="/assets/icons/close.svg"
						alt="Close"
						style={{ width: 24, height: 24, display: "block" }}
					/>
				</button>
				{/* Icon */}
				<div className="flex justify-center mb-4">
					<div className="bg-[#3F2EC8] bg-opacity-10 rounded-full p-3 flex items-center justify-center">
						<svg className="h-9 w-9 text-[#3F2EC8]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
							<path d="M12 18h.01" />
						</svg>
					</div>
				</div>
				<h2 className="text-2xl font-bold text-[#3F2EC8] mb-2">Available on Mobile Only</h2>
				<p className="mb-6 text-gray-700 text-base">
					Chat and negotiate with vendors directly on the Intasida mobile app. Download now to get started!
				</p>
				<div className="flex flex-col sm:flex-row gap-3 items-center">
					<Button className="bg-black h-12 gap-2 text-white hover:bg-black/80 w-full flex-1" asChild>
						<a
							target="_blank"
							rel="noreferrer noopener"
							href="https://play.google.com/store/apps/details?id=com.intasida&pcampaignid=web_share"
						>
							<svg className="size-6" version="1.1" viewBox="0 0 511.999 511.999" fill="#000000">
								<g>
									<path fill="#32BBFF" d="M382.369,175.623C322.891,142.356,227.427,88.937,79.355,6.028 C69.372-0.565,57.886-1.429,47.962,1.93l254.05,254.05L382.369,175.623z" />
									<path fill="#32BBFF" d="M47.962,1.93c-1.86,0.63-3.67,1.39-5.401,2.308C31.602,10.166,23.549,21.573,23.549,36v439.96 c0,14.427,8.052,25.834,19.012,31.761c1.728,0.917,3.537,1.68,5.395,2.314L302.012,255.98L47.962,1.93z" />
									<path fill="#32BBFF" d="M302.012,255.98L47.956,510.035c9.927,3.384,21.413,2.586,31.399-4.103 c143.598-80.41,237.986-133.196,298.152-166.746c1.675-0.941,3.316-1.861,4.938-2.772L302.012,255.98z" />
								</g>
								<path fill="#2C9FD9" d="M23.549,255.98v219.98c0,14.427,8.052,25.834,19.012,31.761c1.728,0.917,3.537,1.68,5.395,2.314 L302.012,255.98H23.549z" />
								<path fill="#29CC5E" d="M79.355,6.028C67.5-1.8,53.52-1.577,42.561,4.239l255.595,255.596l84.212-84.212 C322.891,142.356,227.427,88.937,79.355,6.028z" />
								<path fill="#D93F21" d="M298.158,252.126L42.561,507.721c10.96,5.815,24.939,6.151,36.794-1.789 c143.598-80.41,237.986-133.196,298.152-166.746c1.675-0.941,3.316-1.861,4.938-2.772L298.158,252.126z" />
								<path fill="#FFD500" d="M488.45,255.98c0-12.19-6.151-24.492-18.342-31.314c0,0-22.799-12.721-92.682-51.809l-83.123,83.123 l83.204,83.205c69.116-38.807,92.6-51.892,92.6-51.892C482.299,280.472,488.45,268.17,488.45,255.98z" />
								<path fill="#FFAA00" d="M470.108,287.294c12.191-6.822,18.342-19.124,18.342-31.314H294.303l83.204,83.205 C446.624,300.379,470.108,287.294,470.108,287.294z" />
							</svg>
							<div className="flex flex-col items-start">
								<span className="text-[10px] uppercase leading-tight">Get it on</span>
								<span className="text-sm font-bold leading-tight">Google Play</span>
							</div>
						</a>
					</Button>

					<Button className="bg-black h-12 gap-2 text-white hover:bg-black/80 w-full flex-1" asChild>
						<a
							target="_blank"
							rel="noreferrer noopener"
							href="https://apps.apple.com/us/app/intasida/id6740424971"
						>
							<svg className="size-6" fill="white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 814 1000">
								<path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
							</svg>
							<div className="flex flex-col items-start">
								<span className="text-[10px] leading-tight">Download on the</span>
								<span className="text-sm font-bold leading-tight">App Store</span>
							</div>
						</a>
					</Button>
				</div>
			</div>
		</div>
	);
}
